import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Radio, Megaphone, ShoppingBag, Music2, Mic2, CalendarDays, Sparkles, ArrowRight, X,
  Loader2, CheckCircle2, MapPin, Users, Globe2, Tv,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

// ---------------------------------------------------------------------------
// Packages
// ---------------------------------------------------------------------------
const PACKAGES = [
  {
    id: "programme-sponsorship",
    title: "Programme Sponsorship",
    tagline: "Sponsor a full broadcast programme",
    description:
      "Underwrite an entire HBTV programme — The Kingdom Hour, Judah Talks, Praise Nights or any of our scheduled shows — with an opening and closing sponsor billboard and a mention in every episode across the sponsorship window.",
    icon: Radio,
    accent: "from-[#0B2053] to-[#0B2053]/40",
    perks: ["Opening & closing billboards", "In-episode mentions", "Sponsor logo on schedule", "4-week minimum term"],
  },
  {
    id: "event-promotion",
    title: "Event Promotion",
    tagline: "Promote ministry and community events",
    description:
      "Announce your conference, crusade, launch, or community gathering with a professionally-produced HBTV promo aired multiple times daily in the run-up to the event date.",
    icon: CalendarDays,
    accent: "from-[#0B2053] to-[#0B2053]/40",
    perks: ["Custom 30–45s promo", "Multiple daily airings", "Countdown crawl", "Event day feature"],
  },
  {
    id: "product-business",
    title: "Product & Business Advert",
    tagline: "30 or 60-second slots for Kingdom businesses",
    description:
      "Introduce your product or service to a purpose-driven audience. Choose a 30-second or 60-second commercial slot placed in prime-time bands during broadcasts most relevant to your market.",
    icon: ShoppingBag,
    accent: "from-[#0B2053] to-[#0B2053]/40",
    perks: ["30s or 60s slot", "Prime-time placement", "Weekly rotation report", "Optional voice-over included"],
  },
  {
    id: "music-arts",
    title: "Music & Arts Promotion",
    tagline: "Feature artists, new releases, and interviews",
    description:
      "For Kingdom musicians, worship artists, authors and creatives — get a dedicated feature slot on HBTV: album spotlight, music-video air-play, and an interview segment with the host.",
    icon: Music2,
    accent: "from-[#0B2053] to-[#0B2053]/40",
    perks: ["Album / release spotlight", "Music-video air-play", "Interview segment", "Bio card on-screen"],
  },
  {
    id: "interview-slot",
    title: "Interview Slot",
    tagline: "Book a guest appearance on HBTV",
    description:
      "Sit down with Apostle Blaise Judah on Judah Talks. A curated long-form conversation aired in prime time and archived across HBTV, YouTube, and the ministry's podcast feed.",
    icon: Mic2,
    accent: "from-[#0B2053] to-[#0B2053]/40",
    perks: ["30–45 min feature", "Prime-time slot", "Multi-platform distribution", "Full HD recording delivered"],
  },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function HBTVAdvertise() {
  const [open, setOpen] = useState(null);   // package id | null

  return (
    <div data-testid="advertise-page" className="bg-ink text-white">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-90">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[760px] w-[900px] rounded-full bg-ink-royal/70 blur-[140px]" />
          <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-gold-500/8 blur-[130px]" />
        </div>
        <div className="relative mx-auto max-w-[1200px] px-6 md:px-10 pt-32 md:pt-44 pb-16 md:pb-24 text-center">
          <div className="inline-flex items-center gap-2 border border-gold-500/30 px-4 py-1.5 rounded-full mb-7">
            <Megaphone size={13} className="text-gold-400" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-gold-300">Advertise on HBTV</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-white">
            Reach a growing <span className="font-accent italic text-gold-gradient">African &amp; global</span> audience.
          </h1>
          <p className="mt-7 md:mt-8 mx-auto max-w-2xl text-white/70 text-base md:text-lg leading-relaxed">
            HBTV — House of Blessing TV — is a ministry media channel covering
            <span className="text-gold-300"> Faith · Culture · Agriculture · Environment · Business · &amp; the Arts</span>.
            Partner with a values-aligned platform that speaks Kingdom to a market of purpose-driven viewers.
          </p>
          {/* Stat row */}
          <div className="mt-12 grid grid-cols-3 gap-4 max-w-2xl mx-auto">
            {[
              { icon: Globe2, k: "24/7", v: "Broadcast" },
              { icon: Users, k: "Multi-lingual", v: "8 languages" },
              { icon: MapPin, k: "WAT", v: "Cameroon HQ" },
            ].map(({ icon: Icon, k, v }) => (
              <div key={k} className="border border-gold-500/25 bg-ink-royal/30 px-3 md:px-5 py-4 md:py-5">
                <Icon size={16} className="text-gold-500 mx-auto mb-2" />
                <p className="font-serif text-white text-lg md:text-2xl">{k}</p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.24em] text-white/50">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage strip */}
      <section className="border-t border-gold-500/20 py-10">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 text-center">
          <p className="text-[11px] uppercase tracking-[0.32em] text-gold-500">Programming pillars</p>
          <div className="mt-5 flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-white/75">
            {["Faith", "Culture", "Agriculture", "Environment", "Business", "The Arts"].map((p) => (
              <span key={p} className="inline-flex items-center gap-2 text-sm md:text-base">
                <span className="w-1 h-1 rounded-full bg-gold-500" /> {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28" data-testid="advertise-packages">
        <Reveal className="mb-14">
          <p className="text-[11px] uppercase tracking-[0.28em] text-gold-500 mb-3 flex items-center gap-2"><Sparkles size={12} /> Packages</p>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight max-w-3xl">
            Choose the way you'd like to <span className="font-accent italic text-gold-gradient">show up</span> on HBTV.
          </h2>
        </Reveal>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06}>
              <article
                data-testid={`package-${p.id}`}
                className="group relative h-full flex flex-col border border-gold-500/25 bg-gradient-to-br from-ink-royal/60 via-ink to-ink transition-colors duration-500 hover:border-gold-400"
              >
                {/* Accent bar */}
                <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-gold-400 via-gold-500 to-gold-500/40" />
                <div className="p-7 md:p-8 flex-1 flex flex-col">
                  <div className="w-12 h-12 border border-gold-500/40 bg-gold-500/10 flex items-center justify-center mb-6">
                    <p.icon size={20} className="text-gold-400" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-serif text-2xl md:text-[1.7rem] leading-tight text-white">{p.title}</h3>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-gold-300">{p.tagline}</p>
                  <p className="mt-5 text-white/70 leading-relaxed text-sm">{p.description}</p>
                  <ul className="mt-6 space-y-1.5">
                    {p.perks.map((k) => (
                      <li key={k} className="flex items-start gap-2 text-white/65 text-[13px]">
                        <span className="text-gold-400 mt-1.5 text-[10px]">◆</span>
                        <span>{k}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setOpen(p.id)}
                    data-testid={`book-${p.id}`}
                    className="mt-8 group/btn inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 text-ink font-semibold uppercase tracking-[0.14em] text-xs px-6 py-3.5 transition-colors"
                  >
                    Book This Package
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover/btn:translate-x-0.5" />
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Closing / FAQ nudge */}
      <section className="border-t border-gold-500/15 py-24 text-center" data-testid="advertise-closing">
        <div className="mx-auto max-w-2xl px-6">
          <div className="inline-flex items-center gap-2 border border-gold-500/30 px-4 py-1.5 rounded-full mb-7">
            <Tv size={13} className="text-gold-400" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-gold-300">Have a custom idea?</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight">
            Tell us what you'd like to <span className="font-accent italic text-gold-gradient">broadcast.</span>
          </h2>
          <p className="mt-5 text-white/60 leading-relaxed">
            Our team crafts custom packages when the standard ones don't quite fit — mission trips, seasonal campaigns, book launches, community initiatives.
          </p>
          <button
            onClick={() => setOpen("custom")}
            data-testid="book-custom"
            className="mt-8 inline-flex items-center gap-2 border border-gold-500/40 hover:border-gold-400 text-white/85 hover:text-gold-300 px-8 py-4 text-sm uppercase tracking-[0.16em] transition-colors"
          >
            Enquire About a Custom Package <ArrowRight size={14} />
          </button>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
            <a
              href="/api/hbtv/ratecard.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-ratecard"
              className="text-[11px] uppercase tracking-[0.22em] text-gold-400 hover:text-gold-300 transition-colors inline-flex items-center gap-1.5"
            >
              Download Rate Card (PDF) →
            </a>
            <a
              href="/api/hbtv/podcast.rss"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="podcast-rss"
              className="text-[11px] uppercase tracking-[0.22em] text-gold-400 hover:text-gold-300 transition-colors inline-flex items-center gap-1.5"
            >
              Subscribe on Apple Podcasts / Spotify (RSS) →
            </a>
          </div>
        </div>
      </section>

      {/* Booking modal */}
      <AnimatePresence>
        {open && (
          <BookingModal
            initialPackage={open === "custom" ? "Custom Package" : (PACKAGES.find((p) => p.id === open)?.title || "")}
            onClose={() => setOpen(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Booking modal
// ---------------------------------------------------------------------------
function BookingModal({ initialPackage, onClose }) {
  const [form, setForm] = useState({
    name: "", organization: "", email: "", phone: "",
    package: initialPackage,
    preferred_date: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/hbtv/advertise", form);
      setConfirmMsg(data.message || "Thank you — your enquiry is with our media team.");
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-ink/85 backdrop-blur-md flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto"
      data-testid="booking-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl border border-gold-500/40 bg-gradient-to-br from-ink-royal/70 to-ink shadow-[0_50px_100px_-30px_rgba(212,175,55,0.35)] my-8"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="booking-close"
          className="absolute top-4 right-4 w-10 h-10 rounded-full border border-gold-500/40 text-white/70 hover:text-gold-300 hover:border-gold-400 flex items-center justify-center transition-colors"
        >
          <X size={17} />
        </button>

        {done ? (
          <div className="p-10 text-center" data-testid="booking-success">
            <CheckCircle2 size={54} className="text-gold-400 mx-auto" strokeWidth={1.4} />
            <p className="mt-6 font-serif text-3xl text-white">Enquiry received</p>
            <p className="mt-4 text-white/70 leading-relaxed">{confirmMsg}</p>
            <p className="mt-2 text-white/50 text-sm">A copy of your enquiry has been sent to our HBTV media desk.</p>
            <button
              onClick={onClose}
              data-testid="booking-close-done"
              className="mt-8 inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-ink px-8 py-3 text-xs uppercase tracking-[0.16em] font-semibold transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 md:p-10" data-testid="booking-form">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold-400 mb-2">Booking enquiry</p>
            <h2 className="font-serif text-2xl md:text-3xl text-white leading-tight">
              {form.package}
            </h2>
            <p className="mt-3 text-white/60 text-sm">Share a few details and our HBTV media team will follow up within 48 hours.</p>

            <div className="mt-7 grid gap-4 md:grid-cols-2">
              <Field label="Your name" required>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="field-name" className={fieldCx} />
              </Field>
              <Field label="Organization">
                <input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="Ministry, company, brand" data-testid="field-organization" className={fieldCx} />
              </Field>
              <Field label="Email" required>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="field-email" className={fieldCx} />
              </Field>
              <Field label="Phone" required>
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+237 …" data-testid="field-phone" className={fieldCx} />
              </Field>
              <Field label="Type of advert" required>
                <select value={form.package} onChange={(e) => setForm({ ...form, package: e.target.value })} data-testid="field-package" className={fieldCx}>
                  {["Programme Sponsorship", "Event Promotion", "Product & Business Advert", "Music & Arts Promotion", "Interview Slot", "Custom Package"].map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </Field>
              <Field label="Preferred broadcast date">
                <input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} data-testid="field-date" className={fieldCx} />
              </Field>
            </div>
            <Field label="Short message" required className="mt-4">
              <textarea required rows={5} maxLength={2000} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your goals, audience, and any specific dates or programmes you have in mind." data-testid="field-message" className={`${fieldCx} resize-none`} />
            </Field>

            <button
              type="submit"
              disabled={busy}
              data-testid="booking-submit"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 text-ink font-semibold uppercase tracking-[0.16em] text-sm px-8 py-4 disabled:opacity-70 transition-colors"
            >
              {busy ? (<><Loader2 size={15} className="animate-spin" /> Sending…</>) : "Send Enquiry"}
            </button>
            <p className="mt-4 text-center text-[10px] uppercase tracking-[0.24em] text-white/40">
              We reply within 48 hours · Your details are kept confidential
            </p>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
}

// Small helpers
const fieldCx = "w-full bg-ink border border-gold-500/25 focus:border-gold-400 text-white placeholder:text-white/40 text-sm px-3.5 py-2.5 outline-none transition-colors";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block text-[10px] uppercase tracking-[0.2em] text-white/60 ${className}`}>
      <span>{label}{required && <span className="text-gold-400 ml-1">*</span>}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
